/**
 * ⚖ The WEDGE row — an escape from a lock nothing else can release.
 *
 * ## What this is for
 *
 * `isStreaming` is `activeRunId !== null`, and the ONLY things that clear it are
 * wire events. Three ways a run can stop producing them:
 *
 * | cause | covered by |
 * |---|---|
 * | the runtime RESTARTED | `bootId` (`runtimeDefinitelyRestarted`) — exact, automatic |
 * | the user wants to stop | the existing `abort` → `chat.abort` RPC |
 * | the runtime is ALIVE but WEDGED | **nothing, until this** |
 *
 * The third row is real and measured: the 2026-08-08 `sgb4p9j6` incident ran
 * ~18 minutes with a hung event loop — no sweep fired, the process never died so
 * `bootId` never changed, and no reconnect occurred to trigger the check.
 * **The server `abort` does not help either**: it is an RPC to the very process
 * that is not answering. So the lawyer's composer stays locked with no way out
 * short of a full page reload, on work they may have spent minutes composing.
 *
 * ## ⚖ The three rulings this encodes
 *
 * 1. **NEVER automatic.** A wall-clock timeout that aborts by itself would make
 *    a legitimately long turn kill its own work — trading a stuck promptbox for
 *    a LYING one, and a lie that discards the lawyer's work is strictly worse
 *    than a lock they can see. The comment in `useChatSession` has said so since
 *    `bootId` shipped; this does not change it. We only ever REVEAL a control.
 * 2. **The release is LOCAL.** It clears this browser's run state; it does not
 *    claim the server stopped anything, because it cannot know that and the
 *    process it would ask is the one that is stuck.
 * 3. **Silence is not death.** Crossing the threshold means only "nothing has
 *    arrived for a while", which is exactly what the copy must say. A turn that
 *    is merely slow is indistinguishable from a wedged one from out here, so we
 *    never assert which it is.
 *
 * Pure + IO-free: the clock is injected, so the rules are unit-testable and the
 * component owns no timing logic of its own.
 */

/**
 * How long a run may produce NOTHING before we offer the escape.
 *
 * Chosen to sit far outside normal turn behaviour and far inside the observed
 * wedge: streaming turns emit deltas continuously, so three minutes of total
 * silence is already pathological, while the measured hang lasted ~18 minutes.
 * ⚠ This is the REVEAL delay, never an abort delay — see ruling 1. Being wrong
 * here costs a prematurely-offered button, not a discarded answer.
 */
/**
 * ⚖ 10 minutes, DERIVED (operator directive 2026-08-14, retimed from 3 min).
 *
 * This clock counts silence since the LAST EVENT — chips, deltas and status
 * frames all reset it — so the right size is the longest silent gap a HEALTHY
 * turn produces, not total turn length. Measured max healthy gap: 161s (the
 * compaction specimens' inter-segment figure). The old 3 min was ~1.1× that,
 * which is why the banner nagged on ordinary research turns; 600s is 3.7×
 * margin, still well inside the measured wedge (~18 min).
 *
 * Deliberately EQUAL to the runtime's `NO_ANSWER_SETTLE_MS` (600s): both
 * layers now share one definition of "this silence is suspicious" instead of
 * each inventing its own.
 */
export const RUN_STALL_REVEAL_MS = 10 * 60 * 1000;

/**
 * ⚖ How long a run must be SILENT before persisted history is allowed to
 * conclude it ended — the second half of the I-1 reconcile conjunction.
 *
 * The reconcile's other half is positive evidence: `lx.chat_history` already
 * holds a finalized assistant row for the run the client still believes is
 * streaming. That alone is NOT safe to act on, because a **compaction split
 * shares ONE runId across several agent runs** (see `useChatStore.finalizeRun`)
 * — so a persisted row can belong to an earlier segment of a turn that is still
 * very much alive. Releasing there would end a live turn from the outside,
 * which is exactly the "stuck promptbox traded for a LYING one" that ruling 1
 * above forbids, and the lawyer would lose the answer they are paying for.
 *
 * ⇒ Both must hold: the server has the row AND this client has heard nothing
 * for longer than a healthy turn ever goes quiet.
 *
 * **180s, derived from the same measurement as the reveal delay:** the longest
 * silent gap a healthy turn produces is 161s (the compaction specimens'
 * inter-segment figure). This sits just above it. Deliberately far SHORTER than
 * `RUN_STALL_REVEAL_MS`, because this is not a guess about silence — silence is
 * only the gate on a fact the server already told us.
 *
 * ⚠ Not an abort and not a timeout: nothing here stops any work, and with no
 * persisted row it never fires no matter how long the silence runs. The 10-min
 * reveal remains the answer to "this has been quiet a suspicious while".
 */
export const RUN_RECONCILE_MIN_SILENCE_MS = 180 * 1000;

export interface RunStallInput {
  /** The run the UI believes is live; `null` ⇒ nothing is locked. */
  activeRunId: string | null;
  /** When this session last saw ANY wire activity for that run. `null` ⇒ not
   *  observed yet, which is NOT the same as "long ago" — see below. */
  lastEventAt: number | null;
  now: number;
  thresholdMs?: number;
}

/**
 * Should the UI OFFER the manual release?
 *
 * ⚠ `lastEventAt === null` returns FALSE. "We have not recorded an event yet" is
 * an unknown, and an unknown must not be rendered as a stall — that is the same
 * failed-read-collapsed-into-a-verdict shape this repo keeps ruling against, and
 * it would flash the escape hatch during ordinary boot exactly as the retired
 * role-gate flashed a permissions warning.
 */
export function shouldOfferRunRelease(input: RunStallInput): boolean {
  const { activeRunId, lastEventAt, now, thresholdMs = RUN_STALL_REVEAL_MS } = input;
  if (!activeRunId) return false;
  if (lastEventAt === null) return false;
  // Guard a clock that jumped backwards (sleep/resume, NTP correction): a
  // negative elapsed is nonsense, and treating it as "huge" would offer the
  // escape on a run that is streaming fine.
  const elapsed = now - lastEventAt;
  if (elapsed < 0) return false;
  return elapsed >= thresholdMs;
}
